import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiError, ApiService, Dispositivo, ResultadoImportacaoControlId } from '../../api.service';
import { mensagemResultadoImportacaoControlId } from './regras-importacao-controlid';

@Injectable({ providedIn: 'root' })
export class ServicoTerminalRelogio {
  constructor(private readonly api: ApiService) {}

  novoTerminal(empregadorId = ''): Dispositivo {
    return {
      nome: '',
      codigoServidor: '',
      tipo: 'DESKTOP',
      localizacao: '',
      empregadorId,
      zoomCamera: 1.4,
      areaLeituraCameraPercentual: 72,
      ipLeitor: '',
      usuarioLeitor: '',
      senhaLeitor: '',
      temSenhaLeitor: false,
      ativo: true,
      departamentoIds: []
    };
  }

  prepararEdicao(terminal: Dispositivo, empregadorId = ''): Dispositivo {
    return {
      ...terminal,
      empregadorId: terminal.empregadorId || terminal.empregador?.id || empregadorId,
      zoomCamera: terminal.zoomCamera || 1.4,
      areaLeituraCameraPercentual: terminal.areaLeituraCameraPercentual || 72,
      ipLeitor: terminal.ipLeitor || '',
      usuarioLeitor: terminal.usuarioLeitor || '',
      senhaLeitor: '',
      temSenhaLeitor: !!terminal.temSenhaLeitor,
      departamentoIds: [...(terminal.departamentoIds || [])]
    };
  }

  prepararEnvio(terminal: Dispositivo): Dispositivo {
    return {
      ...terminal,
      nome: (terminal.nome || '').trim(),
      codigoServidor: (terminal.codigoServidor || '').trim(),
      tipo: (terminal.tipo || 'DESKTOP').trim(),
      localizacao: (terminal.localizacao || '').trim(),
      zoomCamera: this.normalizarZoomCamera(terminal.zoomCamera),
      areaLeituraCameraPercentual: this.normalizarAreaLeituraCamera(terminal.areaLeituraCameraPercentual),
      ipLeitor: (terminal.ipLeitor || '').trim(),
      usuarioLeitor: (terminal.usuarioLeitor || '').trim(),
      senhaLeitor: (terminal.senhaLeitor || '').trim() || undefined,
      departamentoIds: [...(terminal.departamentoIds || [])]
    };
  }

  validarTerminal(terminal: Dispositivo) {
    if (!(terminal.nome || '').trim()) {
      return 'Informe o nome do terminal.';
    }
    if (!(terminal.codigoServidor || '').trim()) {
      return 'Informe o codigo de ativacao do terminal.';
    }
    return '';
  }

  listar(empregadorId: string): Observable<Dispositivo[]> {
    return this.api.listarDispositivos(empregadorId);
  }

  listarPorEmpregadorSelecionado(empregadorId: string): Observable<Dispositivo[]> {
    if (!empregadorId) {
      return of([]);
    }

    return this.listar(empregadorId);
  }

  salvar(terminal: Dispositivo, empregadorId: string): Observable<Dispositivo> {
    return this.api.salvarDispositivo({
      ...terminal,
      empregadorId
    });
  }

  private normalizarZoomCamera(zoomCamera?: number) {
    const valor = Number(zoomCamera);
    if (!Number.isFinite(valor)) {
      return 1.4;
    }
    return Math.max(1, Math.min(3, valor));
  }

  private normalizarAreaLeituraCamera(areaLeituraCameraPercentual?: number) {
    const valor = Number(areaLeituraCameraPercentual);
    if (!Number.isFinite(valor)) {
      return 72;
    }
    return Math.max(45, Math.min(100, valor));
  }

  revogarAtivacao(terminal: Dispositivo): Observable<Dispositivo> {
    return this.api.revogarTokenDispositivo(terminal.id || '');
  }

  excluir(terminal: Dispositivo): Observable<void> {
    return this.api.excluirDispositivo(terminal.id || '');
  }

  mensagemEmpregadorObrigatorioTerminal() {
    return 'Selecione um empregador para cadastrar o terminal.';
  }

  mensagemTerminalSalvo(_terminal: Dispositivo, editando = false) {
    return editando
      ? 'Sucesso.\nRegistro alterado com sucesso.'
      : 'Sucesso.\nRegistro salvo com sucesso.';
  }

  mensagemErroSalvarTerminal(erro?: ApiError) {
    if (erro?.status === 401) {
      return 'Sessao expirada. Faca login novamente para salvar o terminal.';
    }
    return 'Erro ao salvar terminal.';
  }

  mensagemConfirmarRevogarAtivacao(terminal: Dispositivo) {
    return `Revogar ativacao do terminal ${terminal.nome}?`;
  }

  mensagemAtivacaoRevogada() {
    return 'Ativacao do terminal revogada.';
  }

  mensagemErroRevogarAtivacao(erro?: ApiError) {
    if (erro?.status === 401) {
      return 'Sessao expirada. Faca login novamente para revogar a ativacao do terminal.';
    }
    return 'Erro ao revogar ativacao do terminal.';
  }

  mensagemConfirmarExclusao(terminal: Dispositivo) {
    return `Excluir terminal ${terminal.nome}?`;
  }

  mensagemTerminalExcluido() {
    return 'Sucesso.\nRegistro excluído com sucesso.';
  }

  mensagemErroExcluirTerminal(erro?: ApiError) {
    if (erro?.status === 401) {
      return 'Sessao expirada. Faca login novamente para excluir o terminal.';
    }
    return 'Erro ao excluir terminal.';
  }

  validarImportacaoControlId(terminal: Dispositivo) {
    if (!terminal.id) {
      return 'Salve o terminal iD Face antes de importar os servidores.';
    }
    if ((terminal.tipo || '').toUpperCase() !== 'IDFACE') {
      return 'Selecione o tipo Control ID iD Face.';
    }
    if (!(terminal.ipLeitor || '').trim()) {
      return 'Informe o IP do iD Face. Se a interface do aparelho nao for a porta 80, use IP:porta (ex. 192.168.0.55:8089).';
    }
    if (!(terminal.usuarioLeitor || '').trim()) {
      return 'Informe o usuario do iD Face.';
    }
    if (!(terminal.senhaLeitor || '').trim() && !terminal.temSenhaLeitor) {
      return 'Informe a senha do iD Face.';
    }
    return '';
  }

  importarControlId(terminal: Dispositivo) {
    return this.api.importarServidoresControlId(terminal.id || '', {
      ipLeitor: (terminal.ipLeitor || '').trim(),
      usuarioLeitor: (terminal.usuarioLeitor || '').trim(),
      senhaLeitor: (terminal.senhaLeitor || '').trim() || undefined
    });
  }

  habilitarFotoMonitor(terminal: Dispositivo) {
    return this.api.habilitarFotoMonitorControlId(terminal.id || '', {
      ipLeitor: (terminal.ipLeitor || '').trim(),
      usuarioLeitor: (terminal.usuarioLeitor || '').trim(),
      senhaLeitor: (terminal.senhaLeitor || '').trim() || undefined
    });
  }

  mensagemFotoMonitorLigada() {
    return 'Envio de foto da batida ligado no iD Face. Bata o ponto de novo e use Ver captura na auditoria.';
  }

  mensagemErroFotoMonitor(erro?: ApiError) {
    if (erro?.status === 401) {
      return 'Sessao expirada. Faca login novamente para ligar o envio de foto.';
    }
    const detalhe = typeof erro?.error?.detail === 'string' ? erro.error.detail : '';
    return detalhe || 'Nao foi possivel ligar o envio de foto no iD Face. O backend precisa alcançar o IP do aparelho.';
  }

  mensagemResultadoImportacao(resultado: ResultadoImportacaoControlId) {
    return mensagemResultadoImportacaoControlId(resultado);
  }

  mensagemErroImportacao(erro?: ApiError) {
    if (erro?.status === 401) {
      return 'Sessao expirada. Faca login novamente para importar os servidores.';
    }
    const detalhe = typeof erro?.error?.detail === 'string' ? erro.error.detail : '';
    return detalhe || 'Nao foi possivel importar os servidores no iD Face.';
  }
}
